import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  getMenuItemsForRole,
  getStateMenuItems,
  getCentralMenuItems,
  isMenuItemActive 
} from '../../constants/sidebarConstants';

const Sidebar = ({ activeModule, onModuleChange, isMobile = false, isCollapsed = false, activeTab = 'budget' }) => {
  const { user, getUserDisplayName, getUserRole } = useAuth();
  const { getResponsiveValue } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const getNavigationItems = () => {
    switch (activeTab) {
      case 'budget':
        return getMenuItemsForRole(getUserRole());
      case 'state':
        return getStateMenuItems(getUserRole());
      case 'central':
        return getCentralMenuItems(getUserRole());
      default:
        return getMenuItemsForRole(getUserRole());
    }
  };

  const navigationItems = getNavigationItems();

  const handleNavigation = (path) => {
    if (path === 'coming-soon') return;
    
    navigate(path);
    if (onModuleChange) {
      onModuleChange(path);
    }
  };

  const handleGroupToggle = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const isActiveItem = (item) => {
    if (item.comingSoon) return false;
    return isMenuItemActive(item, location.pathname);
  };

  const isActiveGroup = (group) => {
    if (group.comingSoon) return false;
    return group.items && group.items.some(item => isActiveItem(item));
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      department: 'bg-blue-100 text-blue-800'
    };
    return colors[role] || 'bg-surface-secondary text-text-secondary';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      department: 'Department User'
    };
    return labels[role] || 'User';
  };

  const getTabDisplayName = () => {
    switch (activeTab) {
      case 'budget':
        return 'Budget Management';
      case 'state':
        return 'State Planning';
      case 'central':
        return 'Central Planning';
      default:
        return 'Planning Portal';
    }
  };

  // Helper to get initials from fullName
  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const names = fullName.trim().split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const containerPadding = getResponsiveValue({
    mobile: 'px-2 pt-4 pb-3',
    tablet: 'px-3 pt-5 pb-4',
    desktop: 'px-4 pt-6 pb-5'
  });

  const buttonPadding = getResponsiveValue({
    mobile: 'px-3 py-3',
    desktop: 'px-4 py-3'
  });

  const textSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  if (!user) {
    return (
      <nav className="nav-sidebar h-screen flex items-center justify-center">
        <div className="text-navigation-text opacity-60">Loading...</div>
      </nav>
    );
  }

  return (
    <nav className="nav-sidebar h-screen flex flex-col">
      {/* Header Section */}
      {(isMobile || !isCollapsed) && (
        <>
          {/* Mobile Header */}
          {isMobile && (
            <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-navigation-hover">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-navigation-active rounded-lg flex items-center justify-center">
                  <span className="text-navigation-text font-semibold text-sm">P</span>
                </div>
                <div>
                  <span className="text-navigation-text font-medium text-base">{getTabDisplayName()}</span>
                  <div className="text-xs text-navigation-text opacity-70">Planning Portal</div>
                </div>
              </div>
            </div>
          )}

          {/* User Info Section - Mobile */}
          {isMobile && (
            <div className="px-3 py-3 border-b border-navigation-hover">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-navigation-active rounded-xl flex items-center justify-center">
                  {user?.profilePicture ? (
                    <img 
                      src={user.profilePicture} 
                      alt={getUserDisplayName()}
                      className="w-10 h-10 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="text-navigation-text text-sm font-semibold">
                      {getInitials(user?.fullName)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-navigation-text font-medium text-sm truncate">
                    {getUserDisplayName()}
                  </div>
                  <div className="text-navigation-text opacity-70 text-xs truncate">
                    {user?.email}
                  </div>
                  <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs mt-1 ${getRoleColor(getUserRole())}`}>
                    {getRoleLabel(getUserRole())}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Collapsed Desktop Header */}
      {!isMobile && isCollapsed && (
        <div className="px-2 pt-4 pb-4 border-b border-navigation-hover flex justify-center">
          <div className="w-10 h-10 bg-navigation-active rounded-xl flex items-center justify-center">
            <span className="text-navigation-text font-semibold text-base">P</span>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <div className={`flex-1 overflow-y-auto scrollbar-thin scrollbar-track-navigation scrollbar-thumb-navigation-hover ${containerPadding}`}>
        <div className="space-y-2">
          {navigationItems.map((item) => {
            if (item.type === 'single') {
              const isActive = isActiveItem(item);
              
              return (
                <div key={item.id} className="relative group">
                  <button
                    onClick={() => handleNavigation(item.path)}
                    disabled={item.comingSoon}
                    className={`
                      w-full flex items-center ${buttonPadding} ${textSize} font-medium rounded-xl
                      transition-all duration-200 group relative
                      ${isActive 
                        ? 'bg-navigation-active text-navigation-text shadow-theme-sm' 
                        : item.comingSoon
                          ? 'text-navigation-text opacity-50 cursor-not-allowed'
                          : 'text-navigation-text hover:bg-navigation-hover'
                      }
                      ${!isMobile && isCollapsed ? 'justify-center' : ''}
                    `}
                    title={item.comingSoon ? `${item.label} - Coming Soon` : item.label}
                  >
                    <div className={`
                      transition-colors duration-200 ${!isMobile && isCollapsed ? '' : 'mr-3'}
                      ${isActive ? 'text-navigation-text' : 'group-hover:text-navigation-text'}
                    `}>
                      {item.icon}
                    </div>
                    
                    {(isMobile || !isCollapsed) && (
                      <>
                        <span className="font-medium truncate">{item.label}</span>
                        
                        {item.comingSoon && (
                          <span className="ml-auto px-2 py-0.5 text-xs bg-navigation-hover text-navigation-text rounded-full opacity-75">
                            Soon
                          </span>
                        )}
                        
                        {isActive && !item.comingSoon && (
                          <div className="ml-auto">
                            <div className="w-2 h-2 rounded-full bg-navigation-text opacity-80"></div>
                          </div>
                        )}
                      </>
                    )}
                  </button>
                  
                  {!isMobile && isCollapsed && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-surface-elevated text-text-primary text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                      {item.comingSoon ? `${item.label} - Coming Soon` : item.label}
                    </div>
                  )}
                </div>
              );
            } 
            
            if (item.type === 'group') {
              const isGroupActive = isActiveGroup(item);
              const isExpanded = expandedGroups.has(item.id) || isGroupActive;
              
              return (
                <div key={item.id} className="relative">
                  <div className="relative group">
                    <button
                      onClick={() => !isCollapsed && !item.comingSoon && handleGroupToggle(item.id)}
                      disabled={item.comingSoon}
                      className={`
                        w-full flex items-center ${buttonPadding} ${textSize} font-medium rounded-xl
                        transition-all duration-200 group
                        ${isGroupActive 
                          ? 'bg-navigation-hover text-navigation-text' 
                          : item.comingSoon
                            ? 'text-navigation-text opacity-50 cursor-not-allowed'
                            : 'text-navigation-text hover:bg-navigation-hover'
                        }
                        ${!isMobile && isCollapsed ? 'justify-center' : ''}
                      `}
                      title={item.comingSoon ? `${item.label} - Coming Soon` : item.label}
                    >
                      <div className={`
                        transition-colors duration-200 ${!isMobile && isCollapsed ? '' : 'mr-3'}
                        ${isGroupActive ? 'text-navigation-text' : 'group-hover:text-navigation-text'}
                      `}>
                        {item.icon}
                      </div>
                      
                      {(isMobile || !isCollapsed) && (
                        <>
                          <span className="font-medium truncate">{item.label}</span>
                          
                          {item.comingSoon && (
                            <span className="ml-auto px-2 py-0.5 text-xs bg-navigation-hover text-navigation-text rounded-full opacity-75">
                              Soon
                            </span>
                          )}
                          
                          {!item.comingSoon && (
                            <div className="ml-auto">
                              <svg 
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          )}
                        </>
                      )}
                    </button>
                    
                    {!isMobile && isCollapsed && (
                      <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-surface-elevated text-text-primary text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                        {item.comingSoon ? `${item.label} - Coming Soon` : item.label}
                      </div>
                    )}
                  </div>
                  
                  {(isMobile || !isCollapsed) && isExpanded && item.items && !item.comingSoon && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.items.map((subItem) => {
                        const isActive = isActiveItem(subItem);
                        
                        return (
                          <button
                            key={subItem.id}
                            onClick={() => handleNavigation(subItem.path)}
                            disabled={subItem.comingSoon}
                            className={`
                              w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg
                              transition-all duration-200 relative
                              ${isActive 
                                ? 'bg-navigation-active text-navigation-text shadow-theme-sm' 
                                : subItem.comingSoon
                                  ? 'text-navigation-text opacity-50 cursor-not-allowed'
                                  : 'text-navigation-text opacity-80 hover:bg-navigation-hover hover:opacity-100'
                              }
                            `}
                          >
                            <span className="truncate">{subItem.label}</span>
                            
                            {subItem.comingSoon && (
                              <span className="ml-auto px-1.5 py-0.5 text-xs bg-navigation-hover text-navigation-text rounded opacity-75">
                                Soon
                              </span>
                            )}
                            
                            {isActive && !subItem.comingSoon && (
                              <div className="ml-auto">
                                <div className="w-1.5 h-1.5 rounded-full bg-navigation-text opacity-80"></div>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            return null;
          })}
        </div>
        
        {navigationItems.length === 0 && (
          <div className="text-center py-8">
            <div className="text-navigation-text opacity-60 text-sm">
              No items available
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Sidebar;